import React from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import Typography from '@mui/material/Typography'

import Dropdown, { ItemObject } from '../../../base/Dropdown/Dropdown'
import { LockIconDark } from '../../../assets/icons/LockIconDark'
import { CardBrandIcon } from '../../../assets/icons'
import WidgetBody from '../../../base/Widget/WidgetBody'
import {
  getFormattedExpiration,
  isCardExpired,
} from '../../../utils/paymentMethods'

const defaultSx = {
  secureContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '32px',
  },
  secureCopy: {
    color: '#00000080',
    fontSize: '0.625rem',
    fontWeight: '400',
    lineHeight: 1.125,
    position: 'relative',
    top: '1px',
  },
}

export interface dropdownType {
  label: string
  className?: string
  required: boolean
}

export type PaymentMethod = {
  value: string
  label: string
  brand: string
  last4: string
  expMonth: string
  expYear: string
}

export interface PaymentMethodListProps {
  title: string
  detail?: string
  buttonContent?: string
  secureLabel?: string
  onSubmit?: () => void
  onClickBack?: () => void
  onClickCreate?: () => void
  dropdown: dropdownType
  paymentMethods: PaymentMethod[]
  paymentMethod: PaymentMethod
  form?: UseFormReturn
  isStart?: boolean
}

/**
 * @param {PaymentMethodListProps} props
 * @param props.title (optional) - The title of the component
 * @param {UseFormReturn} props.form (Optional) - hook for managing forms with ease
 */
const PaymentMethodList = (props: PaymentMethodListProps) => {
  const useFormDefault = props.form || useForm()

  Object.assign(PaymentMethodList.defaultProps.dropdown, props.dropdown)
  Object.assign(
    PaymentMethodList.defaultProps.paymentMethod,
    props.paymentMethod,
  )
  if (props.paymentMethods)
    PaymentMethodList.defaultProps.paymentMethods = props.paymentMethods

  const paymentMethodsWithIcons: ItemObject[] =
    PaymentMethodList.defaultProps.paymentMethods.map(pm => {
      const isDisabled = isCardExpired(pm.expMonth, pm.expYear)
      const itemObject: ItemObject = {
        value: pm.value,
        label:
          pm.label ||
          `${pm.brand} *${pm.last4}\u00A0\u00A0exp ${getFormattedExpiration(
            pm.expMonth,
            pm.expYear,
          )}`,
        icon: <CardBrandIcon brand={pm.brand?.toUpperCase()} />,
        disabled: isDisabled,
        disabledText: isDisabled ? 'This card is expired' : '',
      }
      return itemObject
    })

  const onClickContinue = () => props.onSubmit?.()
  const onClickBack = () => props.onClickBack?.()
  const onClickCreate = () => props.onClickCreate?.()

  return (
    <WidgetBody
      title={props.title}
      onBack={onClickBack}
      otherActions={[{ label: 'add new', action: onClickCreate }]}
      onContinue={onClickContinue}
      key={props.title}
    >
      <Dropdown
        {...PaymentMethodList.defaultProps.dropdown}
        form={useFormDefault}
        items={paymentMethodsWithIcons}
        value={PaymentMethodList.defaultProps.paymentMethod?.value}
      />
      <Box sx={defaultSx.secureContainer}>
        <LockIconDark />

        <Typography sx={defaultSx.secureCopy}>{props.secureLabel}</Typography>
      </Box>
    </WidgetBody>
  )
}

PaymentMethodList.defaultProps = {
  title: 'Payment Method',
  detail: 'Please note a fee may apply to card payments',
  buttonContent: 'Add Payment Method',
  dropdown: {
    name: 'paymentMethod',
    label: 'Select Payment Method',
    required: true,
  },
  paymentMethods: [
    {
      value: '1234',
      brand: '',
      label: '',
      last4: '1234',
      expMonth: '01',
      expYear: '2024',
    },
  ],
  paymentMethod: { label: '', value: '', brand: '' },
  secureLabel:
    'All information is securely stored in a digital vault using AES 256-bit encryption',
  isStart: false,
}

export default PaymentMethodList
